import { VariableType } from 'json-to-graphql-query';

import { Profile } from '@/interfaces/profile';
import { LoginArgs, LoginVariables } from './queryVariables/login';
import { ProfileArgs, ProfileVariables } from './queryVariables/profile';
import { Resource } from './resource';

/**
 * Resource autentykacji
 *
 * @export
 * @class AuthResource
 * @extends {Resource}
 */
export class AuthResource extends Resource {
  /**
   * Logownaie
   *
   * @param {string} email
   * @param {string} password
   * @returns {Promise<{ token: string }>}
   * @memberof AuthResource
   */
  public login(email: string, password: string): Promise<{ token: string; profile: Profile }> {
    const query = this.prepareOptions({
      query: {
        __variables: { ...LoginVariables },
        accountLogin: {
          __args: { ...LoginArgs },
          token: true,
          profile: {
            id: true,
            firstName: true,
            lastName: true,
            pesel: true,
            address: true,
            externalId: true,
            data: true
          }
        }
      }
    });
    return this.query({ query, variables: { login: email, password } }, 'accountLogin');
  }

  /**
   * Rejestracja
   *
   * @param {*} profile
   * @param {Tokens} tokens
   * @returns {Promise<string>}
   * @memberof AuthResource
   */
  public register(profile: any): Promise<string> {
    const mutation = this.prepareOptions({
      mutation: {
        __variables: { ...ProfileVariables },
        accountRegister: { __args: { ...ProfileArgs } }
      }
    });
    return this.mutation({ mutation, variables: { profile } }, 'accountRegister');
  }

  /**
   * Sprawdza uprawnienie użytkownika
   *
   * @param {string[]} permissions
   * @returns {Promise<boolean[]>}
   * @memberof AuthResource
   */
  public checkPermissions(permissions: string[]): Promise<boolean[]> {
    const query = this.prepareOptions({
      query: {
        __variables: { permissions: '[String!]!' },
        checkPermissions: { __args: { permissions: new VariableType('permissions') } }
      }
    });
    return this.query({ query, variables: { permissions } }, 'checkPermissions');
  }

  /**
   * Sprawdza uprawnienia użytkownika do konkretnej encji
   *
   * @param {any[]} permissions
   * @returns {Promise<boolean>}
   * @memberof AuthResource
   */
  public checkPermissionsForExactEntities(permissions: any[]): Promise<boolean> {
    const query = this.prepareOptions({
      query: {
        __variables: { permissions: '[PermissionWithEntityInput!]!' },
        checkPermissionsForExactEntities: { __args: { permissions: new VariableType('permissions') } }
      }
    });
    return this.query({ query, variables: { permissions } }, 'checkPermissionsForExactEntities');
  }

  /**
   * Potwierdzenie konta
   *
   * @param {string} token
   * @param {string} password
   * @returns {Promise<number>}
   * @memberof AuthResource
   */
  public async confirm(token: string, password: string): Promise<number> {
    const mutation = this.prepareOptions({
      mutation: {
        __variables: { password: 'String!', token: 'String!' },
        accountConfirm: {
          __args: { password: new VariableType('password'), token: new VariableType('token') }
        }
      }
    });
    return this.mutation(
      {
        mutation,
        variables: { token, password }
      },
      'accountConfirm'
    );
  }

  /**
   * Reset hasła
   *
   * @param {string} email
   * @returns {Promise<boolean>}
   * @memberof AuthResource
   */
  public resetPassword(email: string): Promise<boolean> {
    const mutation = this.prepareOptions({
      mutation: {
        __variables: { email: 'String!' },
        accountResetPassword: { __args: { email: new VariableType('email') } }
      }
    });
    return this.mutation({ mutation, variables: { email } }, 'accountResetPassword');
  }

  /**
   * Zamiana hasła użytkownika
   *
   * @param {string} token
   * @param {string} password
   * @returns {Promise<boolean>}
   * @memberof AuthResource
   */
  public async setPassword(token: string, password: string): Promise<boolean> {
    const mutation = this.prepareOptions({
      mutation: {
        __variables: { password: 'String!', token: 'String!' },
        accountSetPassword: {
          __args: { password: new VariableType('password'), token: new VariableType('token') }
        }
      }
    });
    return this.mutation({ mutation, variables: { token, password } }, 'accountSetPassword');
  }

  /**
   * Zamiana hasła zalogowanego użytkownika
   *
   * @param {{
   *     userId: string;
   *     currentPassword: string;
   *     password: string;
   *   }} variables
   * @return {*}  {Promise<boolean>}
   * @memberof AuthResource
   */
  public async setPasswordLogged(variables: {
    userId: string;
    currentPassword: string;
    password: string;
  }): Promise<boolean> {
    const mutation = this.prepareOptions({
      mutation: {
        __variables: { password: 'String!', currentPassword: 'String!', userId: 'String!' },
        accountSetPasswordLogged: {
          __args: {
            password: new VariableType('password'),
            currentPassword: new VariableType('currentPassword'),
            userId: new VariableType('userId')
          }
        }
      }
    });
    return this.mutation({ mutation, variables }, 'accountSetPasswordLogged');
  }

  /**
   * Usuwanie własnego konta
   *
   * @param {{userId: string, password: string}} variables
   * @return {*}  {Promise<number>}
   * @memberof AuthResource
   */
  public deleteOwn(variables: { userId: string; password: string }): Promise<boolean> {
    const mutation = this.prepareOptions({
      mutation: {
        __variables: {
          userId: 'String!',
          password: 'String!'
        },
        accountDeleteOwn: {
          __args: {
            userId: new VariableType('userId'),
            password: new VariableType('password')
          }
        }
      }
    });
    return this.mutation({ mutation, variables }, 'accountDeleteOwn');
  }

  /**
   * Potwierdzenie usunięcia
   *
   * @param {string} token
   * @returns {Promise<boolean>}
   * @memberof AuthResource
   */
  public async confirmDelete(token: string): Promise<boolean> {
    const mutation = this.prepareOptions({
      mutation: {
        __variables: { token: 'String!' },
        accountConfirmDelete: {
          __args: { token: new VariableType('token') }
        }
      }
    });
    return this.mutation({ mutation, variables: { token } }, 'accountConfirmDelete');
  }

  /**
   * Odświeżenie tokenu użytkownika
   *
   * @returns {Promise<string>}
   * @memberof AuthResource
   */
  public async refreshToken(): Promise<string> {
    const query = this.prepareOptions({ query: { accountRefreshToken: {} } });
    return this.query({ query }, 'accountRefreshToken', true);
  }
}

export const authResource = new AuthResource();
